/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';
import { useRouter } from 'js/lib/useRouter';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import { getColorFromColorName, isInvertedColor } from 'bundles/unified-common/utils/colors';

import _t from 'i18n!nls/front-page';

const REDIRECT_URL = '/onboarding?src=banner,frontpage';

const IMGPARAMS = {
  quality: 40,
};

const MAX_IMAGE_WIDTH = 333;
const MAX_IMAGE_HEIGHT = 207;

const styles = {
  card: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: var(--cds-spacing-150) var(--cds-spacing-150) 0 var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-200);
  `,
  content: css`
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  border: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  primaryCTA: css`
    margin-top: var(--cds-spacing-300);
  `,
  title: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-100);

    @media (max-width: 340px) {
      font-size: var(--cds-font-size-300);
    }
  `,
  truncateTwoLines: css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: wrap;
    padding: 0 var(--cds-spacing-400);
    margin-left: -100%;
    margin-right: -100%;
    text-align: center;

    ${breakpoints.down('xs')} {
      padding: 0;
    }
  `,
  image: css`
    margin-top: calc(var(--cds-spacing-400) * -1);
  `,
  lazyImage: css`
    /* disable conflicting css from LazyImgix */
    > div {
      padding-bottom: 0;
      height: 100%;
    }

    .lazyload-wrapper {
      display: flex;
      justify-content: center;
      flex-direction: column;
    }
  `,
  prioritizedImage: css`
    height: ${MAX_IMAGE_HEIGHT}px;
  `,
};

export type MobileHeroCardProps = {
  title: string;
  subtitle?: string | null;
  primaryCtaLabel: string;
  primaryCtaLink?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaLink?: string | null;
  imageUrl: string;
  backgroundColor: string;
  prioritizeImageLoading?: boolean;
};

const MobileHeroCard = ({
  title,
  subtitle,
  primaryCtaLabel,
  primaryCtaLink,
  secondaryCtaLabel,
  secondaryCtaLink,
  imageUrl,
  backgroundColor,
  prioritizeImageLoading = false,
}: MobileHeroCardProps) => {
  const trackEvent = useTracker();
  const router = useRouter();

  // tracking / signup code copied from HeroBanner
  const handleSignUpClick = () => {
    trackEvent('click_button', {
      button: { name: 'enroll' },
      pageSection: { sectionName: 'hero_banner' },
    });

    const query = { ...(router?.location?.query || {}), authMode: 'signup' };

    router.replace(
      {
        pathname: router?.location?.pathname || '/',
        query,
      },
      // @ts-expect-error
      { pathname: router?.asPath?.split('?')?.[0], query }
    );
  };

  const cardColorStyle = `background-color: ${getColorFromColorName(backgroundColor)};`;

  const isDarkBackground = isInvertedColor(backgroundColor);

  return (
    <div css={[styles.card, backgroundColor === 'white' ? styles.border : null, cardColorStyle]}>
      <div css={styles.content}>
        <SeoPageHeader />
        <Typography2
          variant="titleSmall"
          component="h2"
          css={[styles.title, styles.truncateTwoLines]}
          color={isDarkBackground ? 'invertBody' : 'body'}
        >
          {title}
        </Typography2>
        {subtitle && (
          <Typography2
            variant="bodySecondary"
            component="h2"
            color={isDarkBackground ? 'invertBody' : 'body'}
            css={styles.truncateTwoLines}
          >
            {subtitle}
          </Typography2>
        )}
        {primaryCtaLink ? (
          <Button
            component={TrackedLink2}
            requireFullyVisible={false}
            withVisibilityTracking={false}
            trackingName="mobile_hero_primary_cta"
            data-elementtiming="frontpage.banner.cta"
            href={primaryCtaLink}
            css={styles.primaryCTA}
            variant={isDarkBackground ? 'primaryInvert' : 'primary'}
            role="button"
          >
            {primaryCtaLabel}
          </Button>
        ) : (
          <Button
            component={TrackedButton}
            requireFullyVisible={false}
            withVisibilityTracking={false}
            trackingName="mobile_hero_primary_cta"
            data={{ copy: primaryCtaLabel, href: REDIRECT_URL }}
            onClick={handleSignUpClick}
            css={styles.primaryCTA}
            variant={isDarkBackground ? 'primaryInvert' : 'primary'}
            role="button"
          >
            {primaryCtaLabel}
          </Button>
        )}

        {secondaryCtaLabel && secondaryCtaLink && (
          <Button
            component={TrackedLink2}
            requireFullyVisible={false}
            withVisibilityTracking={false}
            trackingName="mobile_hero_secondary_cta"
            href={secondaryCtaLink}
            variant={isDarkBackground ? 'ghostInvert' : 'ghost'}
            role="button"
          >
            {secondaryCtaLabel}
          </Button>
        )}
      </div>
      <div css={styles.image}>
        {prioritizeImageLoading ? (
          <ImgixWithPrioritizedLoading
            css={styles.prioritizedImage}
            imgParams={IMGPARAMS}
            maxHeight={MAX_IMAGE_HEIGHT}
            maxWidth={MAX_IMAGE_WIDTH}
            src={imageUrl}
            alt={_t('Hero Image')}
          />
        ) : (
          <div css={styles.lazyImage}>
            <LazyImgix
              src={imageUrl}
              alt={_t('Hero Image')}
              maxHeight={MAX_IMAGE_HEIGHT}
              maxWidth={MAX_IMAGE_WIDTH}
              imgParams={IMGPARAMS}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileHeroCard;
